import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { difference } from 'lodash';
import { useState, useMemo, useEffect, useCallback } from 'react';
import { Row, Col, Button, Select, Table, Cascader } from 'antd';
import { t } from '@/utils/translator';
import LevelHierarchy from '@json/hlmando_material_hierarchy_structure_v2.json';
import Level3MaterialHierarchy from '@json/hlmando_level3_material_structure_v2.json';

export default function MaterialSearchTable() {
  const [materials, setMaterials] = useState([]);
  const [selectedLv1, setSelectedLv1] = useState(null);
  const [selectedLv2, setSelectedLv2] = useState(null);
  const [selectedLv3, setSelectedLv3] = useState(null);
  const [selectedMaterials, setSelectedMaterials] = useState(null);
  const [selectedLevels, setSelectedLevels] = useState([]);

  const [filterLv1, setFilterLv1] = useState([]);
  const [filterLv2, setFilterLv2] = useState([]);
  const [filterLv3, setFilterLv3] = useState([]);
  const [filterMaterials, setFilterMaterials] = useState([]);

  // ✅ Cascader 선택 시 값 갱신
  const onMaterialHierarchyChange = (selectHierarchy) => {
    if (selectHierarchy.length) {
      const _select_lv1 = [...new Set([...selectHierarchy.map((_selected) => _selected[0])])];
      const isSelectLv1All = difference(['STEERING', 'BRAKE', 'SUSPENSION'], _select_lv1);
      if (isSelectLv1All.length === 0) {
        alert(t('대분류는 2개 이상 선택하실 수 없습니다.'));
        return;
      }
    }

    setSelectedLevels(selectHierarchy);
  };

  // ✅ Cascader 닫혔을 때 materials 생성
  const generateLevelMaterialTabelData = useCallback(
    (open) => {
      if (!open && selectedLevels.length > 0) {
        let result = [];
        selectedLevels.forEach((_level) => {
          const [Level1, Level2, Level3] = _level;
          const mats = Level3MaterialHierarchy[Level3];
          if (mats && mats.length) {
            let level_material_joined = mats.map((material) => ({
              Level1,
              Level2,
              Level3,
              Material: material,
            }));

            result = [...result, ...level_material_joined];
          }
        });
        setMaterials(result);
      }
      // else if (!open) {
      //   setMaterials([]);
      // }
    },
    [selectedLevels]
  );

  // ✅ 필터링된 materials
  const filteredMaterials = useMemo(() => {
    return materials.filter((item) => {
      const lv1Match = selectedLv1 ? item.Level1 === selectedLv1 : true;
      const lv2Match = selectedLv2 ? item.Level2 === selectedLv2 : true;
      const lv3Match = selectedLv3 ? item.Level3 === selectedLv3 : true;
      const matMatch = selectedMaterials ? item.Material === selectedMaterials : true;
      return lv1Match && lv2Match && lv3Match && matMatch;
    });
  }, [materials, selectedLv1, selectedLv2, selectedLv3, selectedMaterials]);

  // ✅ 필터 옵션 생성
  useEffect(() => {
    const lv1Set = new Set(),
      lv2Set = new Set(),
      lv3Set = new Set(),
      materialSet = new Set();
    filteredMaterials.forEach((item) => {
      if (item.Level1) lv1Set.add(item.Level1);
      if (item.Level2) lv2Set.add(item.Level2);
      if (item.Level3) lv3Set.add(item.Level3);
      if (item.Material) materialSet.add(item.Material);
    });
    setFilterLv1([...lv1Set].map((v) => ({ label: v, value: v })));
    setFilterLv2([...lv2Set].map((v) => ({ label: v, value: v })));
    setFilterLv3([...lv3Set].map((v) => ({ label: v, value: v })));
    setFilterMaterials([...materialSet].map((v) => ({ label: v, value: v })));
  }, [filteredMaterials]);

  // 선택
  const [selectedMaterialRowKeys, setSelectedMaterialRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const MaterialTableRowSelection = {
    selectedRowKeys: selectedMaterialRowKeys,
    onChange: (selectedRowkeys, selectedRows) => {
      setSelectedMaterialRowKeys(selectedRowkeys);
      setSelectedRows(selectedRows);
    },
  };

  function resetSelectedMaterials() {
    setSelectedMaterialRowKeys([]);
    setSelectedRows([]);
  }

  // 다운로드
  function downloadBySelected() {
    if (selectedRows.length === 0) {
      alert('선택 내역이 없습니다.');
      return;
    }

    downloadMaterial(selectedRows);
  }

  function downloadByAll() {
    const dataToExport = filteredMaterials.length > 0 ? filteredMaterials : materials;
    if (dataToExport.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    downloadMaterial(dataToExport);
  }

  function downloadMaterial(data) {
    if (data.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    const rd_number = Math.floor(Math.random() * 10000000);
    const filename = `Scope3_Cat1_Material_${new Date().toISOString().slice(0, 10)}_${rd_number}.xlsx`;

    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1');

    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, filename);
  }

  // 초기화
  function resetAllMaterials() {
    setMaterials([]);
    setSelectedLv1(null);
    setSelectedLv2(null);
    setSelectedLv3(null);
    setSelectedMaterials(null);
    setSelectedLevels([]);
    setFilterLv1([]);
    setFilterLv2([]);
    setFilterLv3([]);
    setFilterMaterials([]);
  }

  const resetFilters = () => {
    setSelectedLv1(null);
    setSelectedLv2(null);
    setSelectedLv3(null);
    setSelectedMaterials(null);
  };

  return (
    <Row>
      {!materials.length && (
        <Col span={24} className="text-center">
          <div className="mb-3">{t('제품 범주를 선택해주세요')}</div>
          <Cascader
            value={selectedLevels}
            options={LevelHierarchy}
            onChange={onMaterialHierarchyChange}
            onDropdownVisibleChange={generateLevelMaterialTabelData}
            placeholder={t('범주 선택')}
            multiple
            showCheckedStrategy={Cascader.SHOW_CHILD}
          >
            <Button>{t('범주 선택')}</Button>
          </Cascader>
        </Col>
      )}

      {selectedLevels.length > 0 && materials.length > 0 && (
        <Col span={24}>
          <div className="d-flex justify-content-between mb-2">
            <Button onClick={resetAllMaterials}>{t('범주 재선택')}</Button>
            <div>
              <Button
                className="me-2"
                onClick={resetSelectedMaterials}
                disabled={!selectedRows.length}
              >
                {t('선택 취소')}
              </Button>
              <Button className="me-2" onClick={downloadBySelected} disabled={!selectedRows.length}>
                {t('선택 품번 다운로드')}
              </Button>
              <Button onClick={downloadByAll} disabled={!filteredMaterials.length}>
                {t('전체 다운로드')}
              </Button>
            </div>
          </div>
          <Row gutter={12} className="mb-3">
            <Col span={5}>
              <Select
                className="w-100"
                placeholder="Level 1"
                options={filterLv1}
                value={selectedLv1}
                onChange={(v) => {
                  setSelectedLv1(v);
                  setSelectedLv2(null);
                  setSelectedLv3(null);
                  setSelectedMaterials(null);
                }}
                allowClear
              />
            </Col>
            <Col span={5}>
              <Select
                className="w-100"
                placeholder="Level 2"
                options={filterLv2}
                value={selectedLv2}
                onChange={(v) => {
                  setSelectedLv2(v);
                  setSelectedLv3(null);
                  setSelectedMaterials(null);
                }}
                allowClear
              />
            </Col>
            <Col span={5}>
              <Select
                className="w-100"
                placeholder="Level 3"
                options={filterLv3}
                value={selectedLv3}
                onChange={(v) => {
                  setSelectedLv3(v);
                  setSelectedMaterials(null);
                }}
                allowClear
              />
            </Col>
            <Col span={5}>
              <Select
                showSearch
                className="w-100"
                placeholder="Material"
                options={filterMaterials}
                value={selectedMaterials}
                onChange={setSelectedMaterials}
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                allowClear
              />
            </Col>
            <Col span={4}>
              <Button
                className="w-100"
                onClick={resetFilters}
                disabled={!selectedLv1 && !selectedLv2 && !selectedLv3 && !selectedMaterials}
              >
                검색 초기화
              </Button>
            </Col>
          </Row>
          <Row gutter={12}>
            <Col span={24}>
              <Table
                dataSource={filteredMaterials}
                columns={[
                  { title: 'Level 1', dataIndex: 'Level1', key: 'Level1' },
                  { title: 'Level 2', dataIndex: 'Level2', key: 'Level2' },
                  { title: 'Level 3', dataIndex: 'Level3', key: 'Level3' },
                  { title: 'Material', dataIndex: 'Material', key: 'Material' },
                ]}
                rowKey={(record) =>
                  `${record.Level1}-${record.Level2}-${record.Level3}-${record.Material}`
                }
                rowSelection={{
                  type: 'checkbox',
                  ...MaterialTableRowSelection,
                }}
                size="small"
                bordered
              />
            </Col>
          </Row>
        </Col>
      )}
    </Row>
  );
}
